// Enhanced version of your ReceiptProcessingScreen with item editing capabilities

import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Container, 
  Divider, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  Typography, 
  useTheme,
  useMediaQuery,
  Alert,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Grid,
  Collapse,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Drawer,
  Tooltip,
  Badge,
  alpha,
  LinearProgress,
  Slide
} from '@mui/material';
import { 
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayArrowIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  AutoFixHigh as AutoFixIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  TextFields as TextFieldsIcon,
  Psychology as PsychologyIcon,
  DoneAll as DoneAllIcon,
  AccessTime as AccessTimeIcon,
  WifiOff as WifiOffIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBudget } from '../../contexts/BudgetContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { SoftPaywallError, extractSoftPaywall, dispatchSoftPaywall } from '../../utils/softPaywall';

// Helper function for development URL discovery
const getDevelopmentURL = () => {
  const override = localStorage.getItem('SOLDIKEEPER_API_URL');
  if (override) {
    return override;
  }
  
  const savedPort = localStorage.getItem('SOLDIKEEPER_BACKEND_PORT');
  if (savedPort) {
    return `http://localhost:${savedPort}`;
  }
  
  return 'http://localhost:3000';
};

// API Configuration
const API_CONFIG = {
  baseURL: process.env.NODE_ENV === 'production' 
    ? (process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api')
    : (process.env.REACT_APP_API_URL || getDevelopmentURL() + '/api')
};

// Helper function to detect potentially problematic item names
const isProblemticItemName = (description) => {
  if (!description) return true;
  
  const problematicPatterns = [
    /^[^a-zA-Z]*$/, // No letters (only numbers/symbols)
    /^.{1,2}$/, // Too short
    /^.{50,}$/, // Too long
    /[{}|\[\]<>@#$%^&*()+=~`]/g, // Strange symbols
    /\d{5,}/g, // Long number sequences
    /^(TOTALE|TOTAL|PAGAMENTO|CONTANTI|RESTO|VIA|TEL)$/i, // Common non-items
    /^[A-Z]{10,}$/g, // All caps long words (often OCR errors)
  ];
  
  return problematicPatterns.some(pattern => pattern.test(description));
};

// Helper function to suggest fixes for item names
const suggestItemFix = (description) => {
  if (!description) return '';
  
  return description
    // Remove common OCR artifacts
    .replace(/[{}|\[\]<>]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add spaces between camelCase
    .replace(/([0-9]+)([a-zA-Z])/g, '$1 $2') // Space between numbers and letters
    .replace(/([a-zA-Z])([0-9]+)/g, '$1 $2') // Space between letters and numbers
    .trim()
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ReceiptProcessingScreen = ({ receiptData, onComplete, onError }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories, handleSaveTransaction } = useBudget();
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // States for tracking the processing flow
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [receiptDetails, setReceiptDetails] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(t('receipts.processing.preparing', 'Preparing upload...'));
  
  // UI states
  const [showCustomization, setShowCustomization] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [defaultCategory, setDefaultCategory] = useState('');
  const [smartCategory, setSmartCategory] = useState(null);
  const [itemCategories, setItemCategories] = useState({});
  
  // NEW: Item editing states
  const [editedItems, setEditedItems] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [newItemDialog, setNewItemDialog] = useState(false);
  const [deleteItemConfirm, setDeleteItemConfirm] = useState(null);
  const [newItem, setNewItem] = useState({ description: '', amount: '', suggestedCategory: 'Groceries' });
  
  // NEW: Upload progress and polling states
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pollAttempts, setPollAttempts] = useState(0);
  
  // Use refs to prevent duplicate processing
  const processingRef = useRef(false);
  const mountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(`receipt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // R23: Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (deleteItemConfirm !== null) setDeleteItemConfirm(null);
        else if (showItemDialog) setShowItemDialog(false);
        else if (newItemDialog) setNewItemDialog(false);
        else if (showCustomization) setShowCustomization(false);
      }
      if (e.key === 'Enter' && deleteItemConfirm !== null) {
        e.preventDefault();
        actualDeleteItem(deleteItemConfirm);
        setDeleteItemConfirm(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteItemConfirm, showItemDialog, newItemDialog, showCustomization]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const steps = [
    t('receipts.processing.stepUpload', 'Upload Receipt'), 
    t('receipts.processing.stepProcessing', 'Processing'), 
    t('receipts.processing.stepComplete', 'Complete')
  ];

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    const headers = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Helper function to test backend connectivity
  const testBackendConnection = async () => {
    const testPorts = [3000, 3001, 5000, 8000];
    
    for (const port of testPorts) {
      try {
        const testURL = `http://localhost:${port}`;
        const response = await fetch(`${testURL}/api/health`, {
          method: 'GET',
          signal: AbortSignal.timeout(2000)
        });
        
        localStorage.setItem('SOLDIKEEPER_BACKEND_PORT', port.toString());
        return testURL;
        
      } catch (error) {
        // Continue to next port
      }
    }
    
    // Try production URL as fallback
    try {
      const prodURL = 'https://soldikeeper-backend-production.up.railway.app';
      const response = await fetch(`${prodURL}/api/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000)
      });
      
      localStorage.setItem('SOLDIKEEPER_API_URL', prodURL);
      return prodURL;
      
    } catch (error) {
      // Production also failed
    }
    
    return null;
  };

  // Helper function to make API calls with automatic backend discovery
  const makeAPICall = async (endpoint, options = {}) => {
    let baseURL = API_CONFIG.baseURL;
    let url = `${baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers
        }
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorData = null;
        try {
          errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch (e2) {
            // Keep the original error message
          }
        }
        // OCR soft paywall — show modal, never lock account
        if (response.status === 402) {
          const soft = extractSoftPaywall({ status: 402, data: errorData || { code: 'SOFT_PAYWALL', message: errorMessage } });
          if (soft) {
            dispatchSoftPaywall(soft);
            throw new SoftPaywallError(soft);
          }
        }
        const err = new Error(errorMessage);
        err.status = response.status;
        err.data = errorData;
        throw err;
      }

      return response;
      
    } catch (fetchError) {
      // If connection refused, try to find the backend
      if (fetchError.message.includes('Failed to fetch') || 
          fetchError.message.includes('ERR_CONNECTION_REFUSED')) {
        
        const discoveredURL = await testBackendConnection();
        
        if (discoveredURL) {
          const discoveredBase = discoveredURL.endsWith('/api') ? discoveredURL : `${discoveredURL}/api`;
          if (discoveredBase !== baseURL) {
            url = `${discoveredBase}${endpoint}`;
          
            const retryResponse = await fetch(url, {
              ...options,
              headers: {
                ...getAuthHeaders(),
                ...options.headers
              }
            });
          
            if (!retryResponse.ok) {
              let errorMessage = `HTTP ${retryResponse.status}: ${retryResponse.statusText}`;
              let errorData = null;
              try {
                errorData = await retryResponse.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
              } catch (e) {
                try {
                  const errorText = await retryResponse.text();
                  errorMessage = errorText || errorMessage;
                } catch (e2) {
                  // Keep the original error message
                }
              }
              if (retryResponse.status === 402) {
                const soft = extractSoftPaywall({ status: 402, data: errorData || { code: 'SOFT_PAYWALL', message: errorMessage } });
                if (soft) {
                  dispatchSoftPaywall(soft);
                  throw new SoftPaywallError(soft);
                }
              }
              const err = new Error(errorMessage);
              err.status = retryResponse.status;
              err.data = errorData;
              throw err;
            }
          
            return retryResponse;
          }
        }
      }
      
      throw fetchError;
    }
  };

  // Convert file to FormData for upload
  const prepareFileData = (data) => {
    let formData;
    
    if (data instanceof FormData) {
      formData = data;
    } else if (data instanceof File) {
      formData = new FormData();
      formData.append('receipt', data);
    } else if (data instanceof Blob) {
      formData = new FormData();
      formData.append('receipt', data, 'receipt.jpg');
    } else {
      throw new Error('Invalid file format. Expected File, Blob, or FormData object.');
    }
    
    return formData;
  };

  // Get category by name (for better matching)
  const getCategoryByName = (categoryName) => {
    if (!categoryName) return null;
    
    const exactMatch = categories.find(cat => 
      cat.name.toLowerCase() === categoryName.toLowerCase()
    );
    if (exactMatch) return exactMatch;
    
    const partialMatch = categories.find(cat => 
      cat.name.toLowerCase().includes(categoryName.toLowerCase()) ||
      categoryName.toLowerCase().includes(cat.name.toLowerCase())
    );
    if (partialMatch) return partialMatch;
    
    const mappings = {
      'bakery': ['bread', 'bakery', 'baked goods'],
      'dairy': ['dairy', 'milk', 'cheese', 'yogurt'],
      'meat': ['meat', 'protein', 'deli'],
      'vegetables': ['vegetables', 'produce', 'veggies'],
      'fruits': ['fruits', 'produce'],
      'beverages': ['beverages', 'drinks', 'soda']
    };
    
    const lowerCategoryName = categoryName.toLowerCase();
    for (const [key, variations] of Object.entries(mappings)) {
      if (variations.some(variation => 
        lowerCategoryName.includes(variation) || variation.includes(lowerCategoryName)
      )) {
        return categories.find(cat => 
          cat.name.toLowerCase().includes(key) ||
          variations.some(v => cat.name.toLowerCase().includes(v))
        );
      }
    }
    
    return null;
  };

  // Check if an item is using its suggested category
  const isUsingSuggestedCategory = (index, item) => {
    const suggestedCategory = getCategoryByName(item.suggestedCategory);
    return suggestedCategory && itemCategories[index] === suggestedCategory.id;
  };

  // NEW: Get the current description for an item (edited or original)
  const getItemDescription = (index, item) => {
    return editedItems[index]?.description || item.description || `Item #${index + 1}`;
  };

  // NEW: Get the current amount for an item (edited or original)
  const getItemAmount = (index, item) => {
    return editedItems[index]?.amount !== undefined ? editedItems[index].amount : item.amount;
  };

  // NEW: Handle item editing
  const handleEditItem = (index, item) => {
    setEditingItem({
      index,
      description: getItemDescription(index, item),
      amount: getItemAmount(index, item),
      suggestedCategory: item.suggestedCategory
    });
    setShowItemDialog(true);
  };

  // NEW: Save edited item (stores original values for visual diff - R19)
  const handleSaveEditedItem = () => {
    if (editingItem) {
      const originalItem = receiptDetails?.lineItems?.[editingItem.index];
      setEditedItems(prev => {
        const existing = prev[editingItem.index] || {};
        return {
          ...prev,
          [editingItem.index]: {
            ...existing,
            description: editingItem.description,
            amount: parseFloat(editingItem.amount) || 0,
            edited: true,
            originalDescription: existing.originalDescription || originalItem?.description || '',
            originalAmount: existing.originalAmount !== undefined ? existing.originalAmount : (originalItem?.amount || 0)
          }
        };
      });
      setShowItemDialog(false);
      setEditingItem(null);
    }
  };

  // NEW: Auto-fix item name (stores original values for visual diff - R19)
  const handleAutoFixItem = (index, item) => {
    const fixedDescription = suggestItemFix(item.description);
    setEditedItems(prev => {
      const existing = prev[index] || {};
      return {
        ...prev,
        [index]: {
          ...existing,
          description: fixedDescription,
          amount: getItemAmount(index, item),
          edited: true,
          originalDescription: existing.originalDescription || item.description || '',
          originalAmount: existing.originalAmount !== undefined ? existing.originalAmount : (item.amount || 0)
        }
      };
    });
  };

  // NEW: Actually perform item deletion (called after confirmation - R17)
  const actualDeleteItem = (index) => {
    // Remove from selected items
    const newSelected = new Set(selectedItems);
    newSelected.delete(index);
    setSelectedItems(newSelected);
    
    // Mark as deleted
    setEditedItems(prev => ({
      ...prev,
      [index]: { deleted: true }
    }));
  };

  // NEW: Delete item with confirmation (R17)
  const handleDeleteItem = (index) => {
    setDeleteItemConfirm(index);
  };

  // NEW: Add new item
  const handleAddNewItem = () => {
    if (receiptDetails && receiptDetails.lineItems) {
      const newIndex = receiptDetails.lineItems.length;
      const newItemData = {
        description: newItem.description,
        amount: parseFloat(newItem.amount) || 0,
        suggestedCategory: newItem.suggestedCategory
      };
      
      // Add to receipt details
      setReceiptDetails(prev => ({
        ...prev,
        lineItems: [...prev.lineItems, newItemData]
      }));
      
      // Select the new item
      setSelectedItems(prev => new Set([...prev, newIndex]));
      
      // Set category for new item
      const suggestedCategory = getCategoryByName(newItem.suggestedCategory);
      setItemCategories(prev => ({
        ...prev,
        [newIndex]: suggestedCategory?.id || defaultCategory
      }));
      
      // Clear new item form
      setNewItem({ description: '', amount: '', suggestedCategory: 'Groceries' });
      setNewItemDialog(false);
    }
  };

  // Retry handler - resets state without page reload
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setActiveStep(0);
    setProcessingStatus(t('receipts.processing.preparing', 'Preparing upload...'));
    setReceiptDetails(null);
    setUploadProgress(0);
    setPollAttempts(0);
    processingRef.current = false;
    // The useEffect will re-trigger since processingRef is now false
  };

  // Main processing function (keeping your existing logic)
  useEffect(() => {
    if (processingRef.current || !receiptData || !mountedRef.current) {
      return;
    }

    processingRef.current = true;
    
    const processReceipt = async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      
      try {
        // Step 1: Prepare and upload receipt
        setActiveStep(0);
        setProcessingStatus(t('receipts.processing.uploading', 'Uploading your receipt...'));
        setUploadProgress(0);
        
        const formData = prepareFileData(receiptData);

        // Simulate upload progress (since we don't have actual upload progress)
        setUploadProgress(50);

        const uploadResponse = await makeAPICall('/receipts/upload', {
          method: 'POST',
          body: formData,
          signal: controller.signal,
          headers: {
            'x-request-id': requestIdRef.current,
            'x-prevent-duplicate': 'true'
          }
        });

        if (!mountedRef.current) return;

        const uploadResult = await uploadResponse.json();
        
        // Upload complete
        setUploadProgress(100);
        await new Promise(resolve => setTimeout(resolve, 300)); // Brief pause to show completion
        
        if (!uploadResult.receiptId && !uploadResult.id) {
          throw new Error('No receipt ID returned from upload');
        }
        
        const receiptId = uploadResult.receiptId || uploadResult.id;
        
        // Step 2: Wait for processing to complete
        setActiveStep(1);
        setProcessingStatus(t('receipts.processing.aiProcessing', 'Processing your receipt with AI...'));
        setPollAttempts(0);
        
        // Poll for completion
        let attempts = 0;
        const maxAttempts = 30;
        
        while (attempts < maxAttempts && mountedRef.current) {
          // Exponential backoff: 1s for first 3 polls, 2s for next 3, 3s for next 4, then 5s
          const delay = attempts < 3 ? 1000
                      : attempts < 6 ? 2000
                      : attempts < 10 ? 3000
                      : 5000;
          await new Promise(resolve => setTimeout(resolve, delay));
          
          attempts++;
          setPollAttempts(attempts);
          
          try {
            const statusResponse = await makeAPICall(`/receipts/${receiptId}`, {
              signal: controller.signal
            });
            
            const statusData = await statusResponse.json();
            
            if (statusData.receipt?.status === 'processed' || statusData.status === 'processed') {
              // Try to get extracted data
              let extractedData;
              try {
                const extractResponse = await makeAPICall(`/receipts/${receiptId}/extract`, {
                  signal: controller.signal
                });
                extractedData = await extractResponse.json();
              } catch (extractError) {
                extractedData = statusData.receipt || statusData;
              }
              
              setReceiptDetails(extractedData);
              
              // Set smart category
              const detectedSmartCategory = findBestCategory(extractedData);
              setSmartCategory(detectedSmartCategory);
              setDefaultCategory(detectedSmartCategory?.id || (categories.length > 0 ? categories[0].id : ''));
              
              // Initialize item categories
              const initialItemCategories = {};
              if (extractedData.lineItems && extractedData.lineItems.length > 0) {
                setSelectedItems(new Set(extractedData.lineItems.map((_, index) => index)));
                
                extractedData.lineItems.forEach((item, index) => {
                  const suggestedCategory = getCategoryByName(item.suggestedCategory);
                  
                  if (suggestedCategory) {
                    initialItemCategories[index] = suggestedCategory.id;
                  } else {
                    initialItemCategories[index] = detectedSmartCategory?.id || (categories.length > 0 ? categories[0].id : '');
                  }
                });
              }
              setItemCategories(initialItemCategories);
              
              setActiveStep(2);
              setProcessingStatus(t('receipts.processing.success', 'Receipt processed successfully!'));
              setLoading(false);
              return;
              
            } else if (statusData.receipt?.status === 'error' || statusData.status === 'error') {
              const errorMsg = statusData.receipt?.errorMessage || statusData.errorMessage || 'Processing failed';
              throw new Error(errorMsg);
            }
            
          } catch (pollError) {
            if (pollError.name === 'AbortError') return;
            if (attempts === maxAttempts - 1) {
              throw pollError;
            }
          }
          
          setProcessingStatus(`Processing your receipt... (${attempts}/${maxAttempts})`);
        }
        
        throw new Error(t('receipts.processing.errorTimeout', 'Processing timed out after 30 seconds. The receipt may still be processing. Please check back in a moment.'));
        
      } catch (err) {
        if (err.name === 'AbortError' || !mountedRef.current) {
          return;
        }
        // Soft paywall modal already shown — stay on free, no hard error toast
        if (err.name === 'SoftPaywallError' || err.code === 'SOFT_PAYWALL' || err.softPaywall) {
          setLoading(false);
          return;
        }
        
        let userFriendlyError = err.message;
        if (err.message.includes('Failed to fetch')) {
          userFriendlyError = t('receipts.processing.errorConnection', 'Unable to connect to the server. Please check your internet connection and try again.');
        } else if (err.message.includes('500')) {
          userFriendlyError = t('receipts.processing.errorServer', 'Server error occurred while processing your receipt. Please try again in a moment.');
        } else if (err.message.includes('413')) {
          userFriendlyError = t('receipts.processing.errorFileTooLarge', 'File too large. Please select a smaller image file.');
        } else if (err.message.includes('415')) {
          userFriendlyError = t('receipts.processing.errorFileType', 'Unsupported file type. Please select a valid image file (JPG, PNG, etc.).');
        }
        
        setError(userFriendlyError);
        setLoading(false);
      }
    };
    
    processReceipt();
  }, [receiptData, categories]);

  // Smart category detection (keeping your existing logic)
  const findBestCategory = (receiptData) => {
    const merchant = receiptData.merchant?.toLowerCase() || '';
    const lineItems = receiptData.lineItems || [];
    
    // Grocery stores
    if (merchant.includes('walmart') || merchant.includes('kroger') || 
        merchant.includes('safeway') || merchant.includes('target') ||
        merchant.includes('whole foods') || merchant.includes('costco') ||
        lineItems.some(item => item.suggestedCategory?.toLowerCase().includes('groceries'))) {
      return categories.find(cat => 
        cat.name.toLowerCase().includes('groceries') || 
        cat.name.toLowerCase().includes('food')
      );
    }
    
    // Gas stations
    if (merchant.includes('shell') || merchant.includes('exxon') || 
        merchant.includes('bp') || merchant.includes('chevron')) {
      return categories.find(cat => 
        cat.name.toLowerCase().includes('transportation') || 
        cat.name.toLowerCase().includes('gas')
      );
    }
    
    // Restaurants
    if (merchant.includes('restaurant') || merchant.includes('cafe') || 
        merchant.includes('pizza') || merchant.includes('mcdonald')) {
      return categories.find(cat => 
        cat.name.toLowerCase().includes('dining') || 
        cat.name.toLowerCase().includes('restaurant')
      );
    }
    
    // Default to most suggested category from items
    const suggestedCategories = lineItems
      .map(item => item.suggestedCategory)
      .filter(cat => cat && cat !== 'Uncategorized');
    
    if (suggestedCategories.length > 0) {
      const mostCommon = suggestedCategories[0];
      return categories.find(cat => 
        cat.name.toLowerCase().includes(mostCommon.toLowerCase()) ||
        mostCommon.toLowerCase().includes(cat.name.toLowerCase())
      );
    }
    
    return null;
  };

  // Format date values
  const formatDate = (dateString) => {
    if (!dateString) return t('receipts.processing.unknownDate', 'Unknown date');
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return t('receipts.processing.invalidDate', 'Invalid date');
    }
  };

  // Handle saving as single transaction (keeping your existing logic)
  const handleSaveAsTotal = async () => {
    if (!receiptDetails || saving) {
      return;
    }
    
    try {
      setSaving(true);
      
      await handleSaveTransaction({
        description: receiptDetails.merchant || t('receipts.processing.receiptTransaction', 'Receipt Transaction'),
        amount: receiptDetails.totalAmount,
        date: receiptDetails.date || new Date().toISOString().split('T')[0],
        categoryId: defaultCategory,
        type: 'expense',
        receiptId: receiptDetails.receiptId || receiptDetails.id,
        metadata: {
          receiptType: 'total',
          merchant: receiptDetails.merchant,
          itemCount: receiptDetails.lineItems?.length || 0
        }
      });
      
      if (onComplete) {
        onComplete(receiptDetails);
      }
      
    } catch (err) {
      console.error('Error saving transaction:', err);
      
      // Handle subscription errors gracefully
      if (err.response?.status === 403 || 
          err.message?.includes('subscription') ||
          err.message?.includes('No subscription found')) {
        
        setError(t('receipts.processing.errorSubscription', 'Receipt processed successfully! However, saving transactions requires a subscription. Please upgrade your account to save transactions automatically.'));
        
        // Still call onComplete since the receipt was processed successfully
        if (onComplete) {
          onComplete(receiptDetails);
        }
      } else {
        setError(t('receipts.processing.saveFailed', 'Failed to save transaction. Please try again.'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle item selection toggle
  const handleItemToggle = (index) => {
    // Skip deleted items
    if (editedItems[index]?.deleted) return;
    
    const newSelected = new Set(selectedItems);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedItems(newSelected);
  };

  // Handle item category change
  const handleItemCategoryChange = (index, categoryId) => {
    setItemCategories(prev => ({
      ...prev,
      [index]: categoryId
    }));
  };

  // Handle select all / deselect all
  const handleSelectAll = () => {
    const availableItems = receiptDetails.lineItems
      .map((_, index) => index)
      .filter(index => !editedItems[index]?.deleted);
      
    if (selectedItems.size === availableItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(availableItems));
    }
  };

  // Calculate selected total (updated to handle edited amounts)
  const getSelectedTotal = () => {
    if (!receiptDetails?.lineItems) return 0;
    
    return receiptDetails.lineItems.reduce((total, item, index) => {
      if (selectedItems.has(index) && !editedItems[index]?.deleted) {
        const amount = getItemAmount(index, item);
        return total + amount;
      }
      return total;
    }, 0);
  };

  // Handle saving selected items as individual transactions (updated for edited items)
  const handleSaveSelectedItems = async () => {
    if (!receiptDetails || saving) {
      return;
    }
    
    try {
      setSaving(true);
      
      const selectedLineItems = receiptDetails.lineItems
        .map((item, index) => ({ item, index }))
        .filter(({ index }) => selectedItems.has(index) && !editedItems[index]?.deleted);
      
      if (selectedLineItems.length === 0) {
        setError(t('receipts.processing.selectAtLeastOne', 'Please select at least one item to create transactions'));
        setSaving(false);
        return;
      }
      
      const savePromises = selectedLineItems.map(({ item, index }) => {
        const categoryId = itemCategories[index] || defaultCategory || (categories.length > 0 ? categories[0].id : '');
        const description = getItemDescription(index, item);
        const amount = getItemAmount(index, item);
        
        return handleSaveTransaction({
          description: description,
          amount: amount,
          date: receiptDetails.date || new Date().toISOString().split('T')[0],
          categoryId: categoryId,
          type: 'expense',
          receiptId: receiptDetails.receiptId || receiptDetails.id,
          metadata: {
            receiptType: 'itemized',
            originalReceiptId: receiptDetails.receiptId || receiptDetails.id,
            merchant: receiptDetails.merchant,
            receiptTotal: receiptDetails.totalAmount,
            suggestedCategory: item.suggestedCategory,
            itemIndex: index,
            wasEdited: !!editedItems[index]
          }
        });
      });
      
      await Promise.all(savePromises);
      
      if (onComplete) {
        onComplete(receiptDetails);
      }
      
    } catch (err) {
      console.error('Error saving transactions:', err);
      
      // Handle subscription errors gracefully
      if (err.response?.status === 403 || 
          err.message?.includes('subscription') ||
          err.message?.includes('No subscription found')) {
        
        setError(t('receipts.processing.errorSubscription', 'Receipt processed successfully! However, saving transactions requires a subscription. Please upgrade your account to save transactions automatically.'));
        
        // Still call onComplete since the receipt was processed successfully
        if (onComplete) {
          onComplete(receiptDetails);
        }
      } else {
        setError(t('receipts.processing.saveFailed', 'Failed to save transactions. Please try again.'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Count problematic items
  const problematicItemsCount = receiptDetails?.lineItems?.filter((item, index) => 
    !editedItems[index]?.deleted && isProblemticItemName(getItemDescription(index, item))
  ).length || 0;

  // Render overlay modal with all three states
  return (
    <>
    <Dialog
      open={true}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          maxHeight: isMobile ? '100vh' : '90vh',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.97)}, ${alpha(theme.palette.background.paper, 0.95)})`,
          backdropFilter: 'blur(20px)',
          overflow: 'auto',
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: alpha(theme.palette.common.black, 0.6),
          backdropFilter: 'blur(8px)',
        }
      }}
    >
      {/* Close button in top right */}
      <IconButton 
        onClick={() => onError ? onError() : navigate('/dashboard')}
        aria-label="Close processing screen"
        sx={{ 
          position: 'absolute', 
          top: 12, 
          right: 12, 
          zIndex: 10,
          bgcolor: alpha(theme.palette.grey[500], 0.1),
          '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.2) }
        }}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ p: { xs: 2.5, sm: 4 }, pt: { xs: 6, sm: 5 } }}>
        {error ? (
          // ERROR STATE
          <>
            {(() => {
              // Determine error type for appropriate icon
              const isTimeout = error.includes('timed out') || error.includes('timeout');
              const isNetwork = error.includes('network') || error.includes('connection') || error.includes('fetch');
              const ErrorIconComponent = isTimeout ? AccessTimeIcon : isNetwork ? WifiOffIcon : ErrorIcon;
              
              return (
                <Box sx={{ textAlign: 'center' }}>
                  <ErrorIconComponent color="error" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    {t('receipts.processing.failed', 'Receipt Processing Failed')}
                  </Typography>
                  <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                    {error}
                  </Alert>
                  
                  {isTimeout && (
                    <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                      {t('receipts.processing.timeoutHint', 'Your receipt may still be processing. Check your receipts list in a few minutes.')}
                    </Alert>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                    <Button 
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => onError ? onError() : navigate('/dashboard')}
                    >
                      {t('receipts.processing.goBack', 'Go Back')}
                    </Button>
                    <Button 
                      variant="contained" 
                      startIcon={<RefreshIcon />}
                      onClick={handleRetry}
                    >
                      {t('receipts.processing.tryAgain', 'Try Again')}
                    </Button>
                  </Box>
                </Box>
              );
            })()}
          </>
        ) : loading && !receiptDetails ? (
          // LOADING STATE
          <>
            {(() => {
              // Determine current processing stage based on activeStep and pollAttempts
              const processingStages = [
                { 
                  id: 0, 
                  label: t('receipts.processing.stageUploading', 'Uploading'), 
                  icon: CloudUploadIcon, 
                  active: activeStep === 0,
                  complete: activeStep > 0 
                },
                { 
                  id: 1, 
                  label: t('receipts.processing.stageReading', 'Reading text'), 
                  icon: TextFieldsIcon, 
                  active: activeStep === 1 && pollAttempts < 5,
                  complete: activeStep > 1 || (activeStep === 1 && pollAttempts >= 5)
                },
                { 
                  id: 2, 
                  label: t('receipts.processing.stageAnalyzing', 'AI analyzing'), 
                  icon: PsychologyIcon, 
                  active: activeStep === 1 && pollAttempts >= 5 && pollAttempts < 15,
                  complete: activeStep > 1 || (activeStep === 1 && pollAttempts >= 15)
                },
                { 
                  id: 3, 
                  label: t('receipts.processing.stageFinalizing', 'Finalizing'), 
                  icon: DoneAllIcon, 
                  active: activeStep === 1 && pollAttempts >= 15,
                  complete: activeStep > 1
                }
              ];
              
              const currentStageIndex = processingStages.findIndex(stage => stage.active);
              const progressValue = currentStageIndex === -1 ? 0 : ((currentStageIndex + 1) / processingStages.length) * 100;
              
              return (
                <>
                  {/* Animated receipt icon header */}
                  <Box sx={{ 
                    width: 80, height: 80, 
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary.main, 0.15)})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 3,
                    animation: 'float 3s ease-in-out infinite',
                    '@keyframes float': {
                      '0%, 100%': { transform: 'translateY(0)' },
                      '50%': { transform: 'translateY(-8px)' }
                    }
                  }}>
                    <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>

                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                      {steps.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                  
                  {/* Processing Stages Pipeline */}
                  <Box sx={{ mb: 4 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={progressValue} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        mb: 3,
                        backgroundColor: theme => alpha(theme.palette.primary.main, 0.1)
                      }} 
                    />
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {processingStages.map((stage) => {
                        const StageIcon = stage.icon;
                        return (
                          <Box 
                            key={stage.id}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              opacity: stage.complete ? 0.5 : stage.active ? 1 : 0.3,
                              transition: 'opacity 0.3s ease'
                            }}
                          >
                            <Box 
                              sx={{ 
                                width: 40, 
                                height: 40, 
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: theme => stage.complete 
                                  ? alpha(theme.palette.success.main, 0.1)
                                  : stage.active 
                                    ? alpha(theme.palette.primary.main, 0.1)
                                    : alpha(theme.palette.grey[500], 0.05),
                                animation: stage.active ? 'pulse 2s ease-in-out infinite' : 'none',
                                '@keyframes pulse': {
                                  '0%, 100%': { transform: 'scale(1)' },
                                  '50%': { transform: 'scale(1.05)' }
                                }
                              }}
                            >
                              {stage.complete ? (
                                <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                              ) : (
                                <StageIcon 
                                  sx={{ 
                                    color: stage.active ? 'primary.main' : 'text.disabled',
                                    fontSize: 20
                                  }} 
                                />
                              )}
                            </Box>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: stage.active ? 600 : 400,
                                color: stage.complete 
                                  ? 'success.main' 
                                  : stage.active 
                                    ? 'text.primary' 
                                    : 'text.disabled'
                              }}
                            >
                              {stage.label}
                              {stage.complete && ' ✓'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                  
                  {/* Upload Progress for Stage 0 */}
                  {activeStep === 0 && uploadProgress > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {uploadProgress === 100 ? t('receipts.processing.uploadComplete', 'Upload complete ✓') : t('receipts.processing.uploading', 'Uploading your receipt...')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Status Message */}
                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="body1" color="text.secondary" aria-live="polite" role="status">
                      {processingStatus}
                    </Typography>
                    
                    {/* Warning for long processing times */}
                    {pollAttempts > 20 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          {t('receipts.processing.takingLonger', 'Taking longer than usual... Please be patient.')}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                </>
              );
            })()}
          </>
        ) : (
          // SUCCESS STATE
          <>
            {/* Success celebration header */}
            <Box sx={{ 
              width: 90, height: 90, 
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.1)})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 2,
              animation: 'celebrate 0.6s ease-out',
              '@keyframes celebrate': {
                '0%': { transform: 'scale(0)', opacity: 0 },
                '50%': { transform: 'scale(1.2)' },
                '100%': { transform: 'scale(1)', opacity: 1 }
              }
            }}>
              <SuccessIcon sx={{ fontSize: 50, color: 'success.main' }} />
            </Box>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                {t('receipts.processing.successTitle', 'Receipt Processed Successfully')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('receipts.processing.successDesc', 'Your receipt is ready to be added to your transactions.')}
              </Typography>
          
              {/* NEW: Show warning if there are problematic items (R20) */}
              {problematicItemsCount > 0 && (
                <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title={t('receipts.processing.problematicExplanation', 'These items have low OCR confidence, missing amounts, or unclear descriptions')}>
                    <Chip 
                      icon={<WarningIcon />} 
                      label={t('receipts.processing.itemsNeedReview', '{{count}} item(s) may need review', { count: problematicItemsCount })}
                      color="warning" 
                      size="small" 
                      variant="outlined" 
                    />
                  </Tooltip>
                </Box>
              )}
            </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {receiptDetails && (
          <Box>
            {/* Receipt Summary (keeping your existing component) */}
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 3, 
                border: `2px solid ${theme.palette.primary.main}`,
                bgcolor: theme.palette.primary.main + '08'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ReceiptIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
                  <Typography variant="h6">{t('receipts.processing.receiptDetails', 'Receipt Details')}</Typography>
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('receipts.processing.merchant', 'Merchant')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {receiptDetails.merchant || t('receipts.processing.unknown', 'Unknown')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('receipts.processing.date', 'Date')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDate(receiptDetails.date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('receipts.processing.totalAmount', 'Total Amount')}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="primary">
                      {formatAmount(receiptDetails.totalAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('receipts.processing.category', 'Category')}
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={defaultCategory}
                        onChange={(e) => setDefaultCategory(e.target.value)}
                        displayEmpty
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Primary Action - Use Total (keeping your existing logic) */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSaveAsTotal}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
                  '&:hover': {
                    boxShadow: `0 8px 25px ${theme.palette.primary.main}60`,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                {saving ? t('receipts.processing.creatingTransaction', 'Creating Transaction...') : t('receipts.processing.addTransaction', 'Add {{amount}} Transaction', { amount: formatAmount(receiptDetails.totalAmount) })}
              </Button>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('receipts.processing.oneTransactionNote', 'This will create one transaction for the total amount')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('receipts.processing.or', 'OR')}
              </Typography>
            </Divider>

            {/* ENHANCED: Secondary Option - Customize Items with editing capabilities */}
            {receiptDetails.lineItems && receiptDetails.lineItems.length > 0 && (
              <Box>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setShowCustomization(!showCustomization)}
                  endIcon={showCustomization ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ 
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('receipts.processing.customizeItems', 'Customize Individual Items')} ({receiptDetails.lineItems.filter((_, index) => !editedItems[index]?.deleted).length} items found)
                    {problematicItemsCount > 0 && (
                      <Tooltip title={t('receipts.processing.problematicExplanation', 'These items have low OCR confidence, missing amounts, or unclear descriptions')}>
                        <Badge badgeContent={problematicItemsCount} color="warning">
                          <WarningIcon fontSize="small" />
                        </Badge>
                      </Tooltip>
                    )}
                  </Box>
                </Button>
                
                <Collapse in={showCustomization}>
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                          {t('receipts.processing.selectItems', 'Select Items & Categories')} ({selectedItems.size} of {receiptDetails.lineItems.filter((_, index) => !editedItems[index]?.deleted).length})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            onClick={handleSelectAll}
                            variant="outlined"
                          >
                            {selectedItems.size === receiptDetails.lineItems.filter((_, index) => !editedItems[index]?.deleted).length ? t('receipts.processing.deselectAll', 'Deselect All') : t('receipts.processing.selectAll', 'Select All')}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => setNewItemDialog(true)}
                            variant="outlined"
                            startIcon={<AddIcon />}
                          >
                            {t('receipts.processing.addItem', 'Add Item')}
                          </Button>
                        </Box>
                      </Box>
                      
                      <Paper variant="outlined" sx={{ 
                        p: 0, 
                        mb: 2,
                        maxHeight: { xs: 300, sm: 400, md: 500 },
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'divider' }
                      }}>
                        {receiptDetails.lineItems.map((item, index) => {
                          // Skip deleted items
                          if (editedItems[index]?.deleted) return null;
                          
                          const isProblematic = isProblemticItemName(getItemDescription(index, item));
                          const currentDescription = getItemDescription(index, item);
                          const currentAmount = getItemAmount(index, item);
                          
                          return (
                            <Box key={index} sx={{ 
                              p: 2, 
                              borderBottom: index < receiptDetails.lineItems.length - 1 ? 
                                `1px solid ${theme.palette.divider}` : 'none',
                              bgcolor: selectedItems.has(index) ? 
                                theme.palette.action.selected : 'transparent'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Checkbox
                                  checked={selectedItems.has(index)}
                                  onChange={() => handleItemToggle(index)}
                                  color="primary"
                                  sx={{ mt: -0.5 }}
                                />
                                
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="body1" fontWeight={500} sx={{ flex: 1 }}>
                                          {currentDescription}
                                        </Typography>
                                        {isProblematic && (
                                          <Tooltip title={t('receipts.processing.needsReview', 'This item name may need review')}>
                                            <WarningIcon fontSize="small" color="warning" />
                                          </Tooltip>
                                        )}
                                        {editedItems[index] && (
                                          <Tooltip title={t('receipts.processing.wasEdited', 'This item has been edited')}>
                                            <CheckIcon fontSize="small" color="success" />
                                          </Tooltip>
                                        )}
                                      </Box>
                                      
                                      {/* R19: Visual diff for edited items */}
                                      {editedItems[index]?.edited && editedItems[index]?.originalDescription && 
                                        editedItems[index].originalDescription !== getItemDescription(index, item) && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through', mr: 1 }}>
                                            {editedItems[index].originalDescription}
                                          </Typography>
                                          {editedItems[index]?.originalAmount !== undefined && 
                                            editedItems[index].originalAmount !== getItemAmount(index, item) && (
                                            <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                              ({formatAmount(editedItems[index].originalAmount)})
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                      
                                      {/* Item action buttons */}
                                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                                        <Tooltip title={t('receipts.processing.editItem', 'Edit item')}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleEditItem(index, item)}
                                            aria-label="Edit item"
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        
                                        {isProblematic && (
                                          <Tooltip title={t('receipts.processing.autoFix', 'Auto-fix item name')}>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleAutoFixItem(index, item)}
                                              color="primary"
                                              aria-label="Auto-fix item name"
                                            >
                                              <AutoFixIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        
                                        <Tooltip title={t('receipts.processing.deleteItem', 'Delete item')}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteItem(index)}
                                            color="error"
                                            aria-label="Delete item"
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Box>
                                    
                                    <Typography variant="body1" fontWeight={600} sx={{ ml: 2 }}>
                                      {formatAmount(currentAmount)}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {t('receipts.processing.aiSuggested', 'AI Suggested:')}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={item.suggestedCategory || 'Uncategorized'}
                                      color={item.suggestedCategory && item.suggestedCategory !== 'Uncategorized' ? 'success' : 'default'}
                                      sx={{ height: 20, fontSize: '0.75rem' }}
                                    />
                                  </Box>
                                  
                                  <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                                    <InputLabel>{t('receipts.processing.category', 'Category')}</InputLabel>
                                    <Select
                                      value={itemCategories[index] || ''}
                                      onChange={(e) => handleItemCategoryChange(index, e.target.value)}
                                      label="Category"
                                    >
                                      {categories.map((category) => {
                                        const isOriginalSuggestion = getCategoryByName(item.suggestedCategory)?.id === category.id;
                                        const isSmartDefault = smartCategory?.id === category.id;
                                        
                                        return (
                                          <MenuItem key={category.id} value={category.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                              <Typography variant="body2" sx={{ flex: 1 }}>
                                                {category.name}
                                              </Typography>
                                              {isOriginalSuggestion && (
                                                <Chip size="small" label="AI" color="success" sx={{ height: 16, fontSize: '0.7rem' }} />
                                              )}
                                              {!isOriginalSuggestion && isSmartDefault && (
                                                <Chip size="small" label="Default" color="primary" sx={{ height: 16, fontSize: '0.7rem' }} />
                                              )}
                                            </Box>
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {getCategoryByName(item.suggestedCategory) 
                                        ? `Using AI suggestion: "${item.suggestedCategory}"` 
                                        : `No exact match for "${item.suggestedCategory || 'None'}", using smart default`
                                      }
                                    </Typography>
                                  </FormControl>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Paper>

                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          Will create {selectedItems.size} individual transaction(s) for {formatAmount(getSelectedTotal())}
                        </Typography>
                        <Typography variant="body2">
                          Category Assignment: {
                            receiptDetails.lineItems.filter((item, index) => 
                              selectedItems.has(index) && !editedItems[index]?.deleted && isUsingSuggestedCategory(index, item)
                            ).length
                          } items using AI suggestions, {
                            selectedItems.size - receiptDetails.lineItems.filter((item, index) => 
                              selectedItems.has(index) && !editedItems[index]?.deleted && isUsingSuggestedCategory(index, item)
                            ).length
                          } using smart defaults
                        </Typography>
                      </Alert>

                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSaveSelectedItems}
                        disabled={saving || selectedItems.size === 0}
                        startIcon={saving ? <CircularProgress size={20} /> : <ShoppingCartIcon />}
                        sx={{ 
                          py: 1.2,
                          borderRadius: 2,
                          fontWeight: 600
                        }}
                      >
                        {saving ? t('receipts.processing.creatingTransactions', 'Creating Transactions...') : t('receipts.processing.createTransactions', 'Create {{count}} Transaction(s)', { count: selectedItems.size })}
                      </Button>
                    </CardContent>
                  </Card>
                </Collapse>
              </Box>
            )}
          </Box>
        )}
          </>
        )}
      </Box>
    </Dialog>

      {/* R15: Item editing - Bottom Drawer on mobile, Dialog on desktop */}
      {isMobile ? (
        <Drawer 
          anchor="bottom" 
          open={showItemDialog} 
          onClose={() => setShowItemDialog(false)}
          PaperProps={{ 
            sx: { 
              borderTopLeftRadius: 16, 
              borderTopRightRadius: 16, 
              maxHeight: '80vh', 
              p: 3 
            } 
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('receipts.processing.editItem', 'Edit Item')}
          </Typography>
          <TextField
            fullWidth
            label={t('receipts.processing.itemDescription', 'Description')}
            value={editingItem?.description || ''}
            onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, description: e.target.value }) : prev)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('receipts.processing.itemAmount', 'Amount')}
            type="number"
            inputProps={{ step: '0.01' }}
            value={editingItem?.amount ?? ''}
            onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, amount: e.target.value }) : prev)}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setShowItemDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="contained" onClick={handleSaveEditedItem}>
              {t('common.save', 'Save')}
            </Button>
          </Box>
        </Drawer>
      ) : (
        <Dialog open={showItemDialog} onClose={() => setShowItemDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('receipts.processing.editItem', 'Edit Item')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={t('receipts.processing.itemDescription', 'Description')}
              value={editingItem?.description || ''}
              onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, description: e.target.value }) : prev)}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth
              label={t('receipts.processing.itemAmount', 'Amount')}
              type="number"
              inputProps={{ step: '0.01' }}
              value={editingItem?.amount ?? ''}
              onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, amount: e.target.value }) : prev)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowItemDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="contained" onClick={handleSaveEditedItem}>
              {t('common.save', 'Save')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* R17: Delete item confirmation dialog */}
      <Dialog open={deleteItemConfirm !== null} onClose={() => setDeleteItemConfirm(null)}>
        <DialogTitle>{t('receipts.processing.removeItem', 'Remove Item?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('receipts.processing.removeItemConfirm', 'This item will be excluded from transaction creation.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteItemConfirm(null)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            color="error" 
            onClick={() => { 
              actualDeleteItem(deleteItemConfirm); 
              setDeleteItemConfirm(null); 
            }}
          >
            {t('common.remove', 'Remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReceiptProcessingScreen;